/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BackupModel } from '../../models';
import { ComponentRef, Injectable, ViewContainerRef } from '@angular/core';
import { ConfirmPasswordComponent } from '../../../../../core/components';
import {
    ConnectionDetailsService,
    DownloadService,
    KeyStoreService,
    NotifyService,
    PGPService
} from '../../../../../core/services';
import { ErrorResponseModel } from '../../../../../core/models';
import { HttpEventType } from '@angular/common/http';
import { Observable } from 'rxjs';
import { VendorEnum } from '../../enums';
import { concatMap } from 'rxjs/operators';

@Injectable()
export class BackupUtilsService {
    /**
     * Modal Component Reference.
     * @private
     */
    private componentReference: ComponentRef<any>;

    constructor(
        private downloadService: DownloadService,
        private connectionDetailsService: ConnectionDetailsService,
        private keyStoreService: KeyStoreService,
        private pgpService: PGPService
    ) {
    }

    /**
     * Start download.
     * @param row
     * @param notifyService
     * @param modalContainer
     */
    public startDownload(row: any, notifyService: NotifyService, modalContainer: ViewContainerRef): Observable<any> {
        return new Observable<any>(observer => {
            if (!this.keyStoreService.orgPrivateKey || this.keyStoreService.orgPrivateKey === 'null') {
                notifyService.info(
                    'storage_download_restriction_notification',
                    { static: true, messageTranslate: true }
                );
                observer.next();
                observer.complete();
            } else {
                this.getUserPasswordHelper(modalContainer)
                    .pipe(concatMap((password: string) => this.startDownloadProcess(row, password)))
                    .subscribe({
                        next: (): void => {
                            observer.next();
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        });
    }

    /**
     * Start download process.
     * @param row
     * @param password
     */
    public startDownloadProcess(row: BackupModel, password: any): Observable<any> {
        return new Observable<any>(observer => {
            this.getFile(row, password)
                .pipe(
                    concatMap(({ password, file }) => this.formatFileForDecryption(password, file)),
                    concatMap(({ password, fileArrayBuffer }) => this.decryptOrgKey(password, fileArrayBuffer)),
                    concatMap((
                        {
                            decryptedOrgKey,
                            fileArrayBuffer
                        }) => this.decryptFileWithOrgKey(decryptedOrgKey, fileArrayBuffer)
                    ),
                    concatMap(decryptedFile => this.downloadDecryptedFile(decryptedFile, row)),
                )
                .subscribe({
                    next: (): void => {
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    },
                    complete: () => {
                        // Destroy the component on complete.
                        if (this.componentReference) {
                            this.componentReference.destroy();
                        }
                    }
                });
        });
    }

    /**
     * Get user password helper function.
     * @param modalContainer
     */
    public getUserPasswordHelper(modalContainer: any): Observable<any> {
        return new Observable<any>(observer => {
            this.getUserPassword(modalContainer)
                .subscribe({
                    next: (password: any): void => {
                        observer.next(password);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Prompt user for password.
     * @param modalContainer
     * @private
     */
    private getUserPassword(modalContainer: any): Observable<any> {
        return new Observable<any>(observer => {
            const confirmPasswordModal = modalContainer.createComponent(ConfirmPasswordComponent);
            confirmPasswordModal.instance.message = 'backup_download_message';
            confirmPasswordModal.instance.closeModalInternally = false;
            confirmPasswordModal.instance.callBack = (password: string) => {
                observer.next(password);
                observer.complete();
            };
            // Save to class variable for destroy later.
            this.componentReference = confirmPasswordModal;
        });
    }

    /**
     * Get the file from the server.
     * @param row
     * @param password
     * @private
     */
    private getFile(row: BackupModel, password: any): Observable<any> {
        return new Observable<any>(observer => {
            const apiUrl = this.connectionDetailsService.getUrl();
            const url = `${apiUrl}/${row.namespaceId}/backups/${row.id}/download`;
            this.downloadService.download(url, 'json')
                .subscribe({
                    next: (result: any): void => {
                        if (result.type === HttpEventType.Response) {
                            const file = result.body.data.backupData;
                            observer.next({ password, file });
                            observer.complete();
                        }
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Convert file into a format for the decrypt function.
     * @param password
     * @param fileData
     * @private
     */
    private formatFileForDecryption(password: string, fileData: string): Observable<any> {
        return new Observable<any>(observer => {

            const raw = atob(fileData);
            const rawLength = raw.length;
            const fileArrayBuffer = new Uint8Array(new ArrayBuffer(rawLength));

            for (let i = 0; i < rawLength; i++) {
                fileArrayBuffer[i] = raw.charCodeAt(i);
            }

            observer.next({ password, fileArrayBuffer });
            observer.complete();
        });
    }

    /**
     * Decrypt the organisation key.
     * @param password
     * @param fileArrayBuffer
     * @private
     */
    private decryptOrgKey(password: string, fileArrayBuffer: any): Observable<any> {
        return new Observable<any>(observer => {
            const userPrivateKey = this.keyStoreService.userPrivateKey;
            const orgPrivateKey = this.keyStoreService.orgPrivateKey;
            this.pgpService.getDecryptedOrgKeyWithUserPassword(password, userPrivateKey, orgPrivateKey)
                .subscribe({
                    next: (decryptedOrgKey: any): void => {
                        observer.next({ decryptedOrgKey, fileArrayBuffer });
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Decrypt the download.
     * @param decryptedOrgPrivateKey
     * @param file
     * @private
     */
    private decryptFileWithOrgKey(decryptedOrgPrivateKey: any, file: any): Observable<any> {
        return new Observable<any>(observer => {
            this.pgpService.decryptFileWithKey(decryptedOrgPrivateKey, file)
                .subscribe({
                    next: (decryptedFile: any): void => {
                        observer.next(decryptedFile);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Download file.
     * @param decryptedFile
     * @param row
     * @private
     */
    private downloadDecryptedFile(decryptedFile: BlobPart, row: BackupModel): Observable<any> {
        return new Observable<any>(observer => {
            const fileName = row.filename.split('.', 1)[0] || row.filename;
            const options = this.getVendorOptions(row);
            this.downloadService.generateDownload(decryptedFile, fileName, options);
            observer.next();
            observer.complete();
        });
    }

    /**
     * Get vendor options.
     * @param row
     * @private
     */
    private getVendorOptions(row: any): any {
        if (row.adcVendorId === VendorEnum.F5) {
            return { type: 'application/x-gzip', extension: '.ucs' };
        }

        return { type: 'application/zip' };
    }
}
